import dayjs from 'dayjs';

// Appointment edit log (appointmentDeliveryData.edits[].changes = [{ field, from, to }]) → readable text.
// Values are stored raw (ISO dates, S3 keys). Used by the history panel, the Edited Appointments page and its export.

const sameYear = (d) => d.year() === dayjs().year();
const fmtDay = (d) => d.format(sameYear(d) ? 'D MMM' : 'D MMM YYYY');
const fmtTime = (d) => d.format(d.minute() ? 'h:mm A' : 'h A');
const fmtDateTime = (v) => (v ? `${fmtDay(dayjs(v))}, ${fmtTime(dayjs(v))}` : 'none');
const fmtDate = (v) => (v ? fmtDay(dayjs(v)) : 'none');
// "13 Oct, 8 AM–10 AM"
const fmtSlot = (start, end) => {
    if (!start) return 'none';
    if (!end) return fmtDateTime(start);
    return `${fmtDay(dayjs(start))}, ${fmtTime(dayjs(start))}–${fmtTime(dayjs(end))}`;
};

const EDIT_FIELDS = {
    appointment_del_start: { label: 'Start', fmt: fmtDateTime },
    appointment_del_end: { label: 'End', fmt: fmtDateTime },
    edd: { label: 'EDD', fmt: fmtDateTime },
    appointment_del_po: { label: 'PO' },
    appointment_del_po_expiry: { label: 'PO Expiry', fmt: fmtDate },
    appointment_del_remarks: { label: 'Remarks' },
    appointment_del_alt_contact_1: { label: 'Contact 1' },
    appointment_del_alt_contact_2: { label: 'Contact 2' },
    appointment_del_invoice_s3_key: { label: 'Invoice', doc: true },
    appointment_del_eway_bill_s3_key: { label: 'E-Way Bill', doc: true },
    appointment_del_po_copy_s3_key: { label: 'PO Copy', doc: true },
    appointment_del_extra_docs: { label: 'Documents added', added: true },
};

const changeText = (c) => {
    const f = EDIT_FIELDS[c.field] || {};
    if (f.doc) return c.from ? 'replaced' : 'uploaded';
    if (f.added) return c.to || '';
    const show = (v) => (v ? (f.fmt ? f.fmt(v) : v) : 'none');
    return `${show(c.from)} → ${show(c.to)}`;
};

export const formatEditChange = (c) => {
    const f = EDIT_FIELDS[c.field] || { label: c.field };
    return f.doc ? `${f.label} ${changeText(c)}` : `${f.label}: ${changeText(c)}`;
};

// Fixed-document fields in the edit log → docType for /appointment_delivery/doc_url.
export const EDITED_DOC_TYPES = {
    appointment_del_invoice_s3_key: 'INVOICE',
    appointment_del_eway_bill_s3_key: 'EWAY_BILL',
    appointment_del_po_copy_s3_key: 'PO_COPY',
};

// Changes other than the fixed documents (those are shown with View old / View new links).
export const nonDocChanges = (changes) => (changes || []).filter((c) => !EDITED_DOC_TYPES[c.field]);

// Non-document changes as display lines: one "Slot" line for start + end, then EDD (flagged), then the rest.
export const summarizeChanges = (changes) => {
    const list = nonDocChanges(changes);
    const byField = (f) => list.find((c) => c.field === f);
    const start = byField('appointment_del_start');
    const end = byField('appointment_del_end');
    const lines = [];
    if (start && end) {
        lines.push({ key: 'slot', label: 'Slot', text: `${fmtSlot(start.from, end.from)} → ${fmtSlot(start.to, end.to)}` });
    }
    const edd = byField('edd');
    if (edd) lines.push({ key: 'edd', label: 'EDD', text: changeText(edd), strong: true });
    list.forEach((c) => {
        if (c.field === 'edd' || (start && end && (c === start || c === end))) return;
        lines.push({ key: c.field, label: (EDIT_FIELDS[c.field] || {}).label || c.field, text: changeText(c) });
    });
    return lines;
};

// All changes as one line of text (exports, compact views).
export const summarizeChangesText = (changes) => [
    ...summarizeChanges(changes).map((l) => `${l.label}: ${l.text}`),
    ...(changes || []).filter((c) => EDITED_DOC_TYPES[c.field]).map(formatEditChange),
].join(' · ');

// How the appointment date moved: { label, tone } or null when the date/slot didn't change.
export const getMoveTag = (changes) => {
    const start = (changes || []).find((c) => c.field === 'appointment_del_start');
    if (!start?.from || !start?.to) return null;
    const days = dayjs(start.to).startOf('day').diff(dayjs(start.from).startOf('day'), 'day');
    if (days > 0) return { label: `Postponed +${days} day${days > 1 ? 's' : ''}`, tone: 'amber' };
    if (days < 0) return { label: `Preponed −${-days} day${days < -1 ? 's' : ''}`, tone: 'green' };
    return { label: 'Same day, new slot', tone: 'grey' };
};

// Editor email, only when it adds something to the name (often the username is the email).
export const editorEmailIfDifferent = (name, email) =>
    email && (!name || name.trim().toLowerCase() !== email.trim().toLowerCase()) ? email : '';
